import { useLocation } from "@remix-run/react";
import { useCallback, useEffect, useRef, useState } from "react";

// * This is meant to prolong submission(or loading state) of the current action especially when the server has to perform
// * a redirect, which doesn't reflect in the client until the next page is ready to render

export function useProlongedNavigationState(
  timeoutMs = 1000 * 60 * 5 // default: 5 minutes - Maintain navigation state for 5 minutes
) {
  const location = useLocation();
  const [isNavigating, setIsNavigating] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Emergency reset function that bypasses normal timeout behavior
  const reset = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    setIsNavigating(false);
  }, []);

  useEffect(() => {
    if (isNavigating) {
      // Clear any existing timer when setting to true (for new navigation)
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // Set new timer
      timerRef.current = setTimeout(() => {
        setIsNavigating(false);
        timerRef.current = null;
      }, timeoutMs);
    }
    // Note: We DON'T clear the timer when isNavigating becomes false
    // This allows the prolonged navigation state to work properly

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [location, isNavigating, timeoutMs]);

  return {
    isNavigating,
    setIsNavigating,
    reset,
  };
}
